import { useState } from "react";
import { ArrowRight, Download, ExternalLink, Lock, Play, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { TopoLines } from "./Decor";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.yetitraining.app";

const StoreAvailability = () => (
  <section id="download" className="scroll-mt-20 bg-[#EAF3ED] py-20 lg:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <div className="reveal max-w-xl">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#D6EAE0] text-[#2F7555]">
            <Download size={23} aria-hidden="true" />
          </span>
          <p className="eyebrow mt-6 text-[#2F7555]">Download AKTUA</p>
          <h2 className="section-title mt-3 text-4xl text-[#202622] sm:text-5xl">
            AKTUA is now on Google Play.
          </h2>
          <p className="mt-5 text-base font-medium leading-relaxed text-[#65706A] md:text-lg">
            Download AKTUA on Android today. The iOS App Store release is still pending, with English and Spanish available across the core experience.
          </p>
          <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" data-testid="google-play-download-link" className="mt-7 inline-flex min-h-[52px] items-center gap-3 rounded-xl bg-[#3F8F68] px-6 text-sm font-bold text-white transition-colors hover:bg-[#2F7555] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7555] focus-visible:ring-offset-4" aria-label="Get AKTUA on Google Play, opens in a new tab">
            <Play size={18} fill="currentColor" aria-hidden="true" /> Get it on Google Play <ExternalLink size={15} aria-hidden="true" />
          </a>
          <p className="mt-3 text-sm text-[#65706A]">On a computer? Scan the code with your Android phone.</p>
        </div>

        <div className="reveal relative overflow-hidden rounded-[2rem] bg-[#041F1A] p-5 shadow-[0_24px_70px_-44px_rgba(4,31,26,0.8)] sm:p-7" style={{ transitionDelay: "100ms" }}>
          <TopoLines className="pointer-events-none absolute -right-36 -top-28 text-[#8BE8C7] opacity-[0.08]" />
          <div className="relative space-y-4">
            <div data-testid="store-availability-google-play" className="rounded-2xl border border-[#8BE8C7]/30 bg-[#0B3A31] p-5 sm:grid sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6 sm:p-6">
              <div className="min-w-0">
                <div className="flex items-center gap-4">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#8BE8C7] text-[#041F1A]">
                    <Play size={22} fill="currentColor" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-[#8BE8C7]">Available now</p>
                    <p className="mt-1 text-xl font-bold text-[#F3F7F5]">Google Play</p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#B8C8C3]">Open the verified AKTUA listing on your Android device.</p>
              </div>
              <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" className="mt-5 inline-flex rounded-2xl bg-[#F7F3EA] p-2 shadow-sm transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8BE8C7] focus-visible:ring-offset-4 focus-visible:ring-offset-[#0B3A31] sm:mt-0" aria-label="Scan or open the AKTUA Google Play listing">
                <QRCodeSVG value={GOOGLE_PLAY_URL} size={144} level="M" marginSize={4} bgColor="#F7F3EA" fgColor="#041F1A" title="QR code for the AKTUA Google Play listing" />
              </a>
            </div>

            <div data-testid="store-availability-app-store" role="status" aria-label="App Store availability: pending" className="rounded-2xl border border-[#8BE8C7]/15 bg-[#062B24]/90 p-5 sm:flex sm:items-center sm:gap-5 sm:p-6">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#8BE8C7]/10 text-[#8BE8C7]">
                <Smartphone size={22} aria-hidden="true" />
              </span>
              <div className="mt-4 min-w-0 flex-1 sm:mt-0">
                <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-[#819D95]">For iPhone</p>
                <p className="mt-1 text-xl font-bold text-[#F3F7F5]">App Store</p>
                <p className="mt-1 text-sm text-[#9FB4AE]">iOS TestFlight testing is underway</p>
              </div>
              <span className="mt-4 inline-flex min-h-[36px] shrink-0 items-center rounded-full border border-[#8BE8C7]/25 bg-[#8BE8C7]/[0.08] px-4 text-xs font-bold text-[#8BE8C7] sm:mt-0">Pending</span>
            </div>
            <p className="px-2 pt-1 text-center text-xs leading-relaxed text-[#819D95]">The QR code opens the same Google Play listing as the download button.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FinalCta = () => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (event) => {
    event.preventDefault();
    if (!email.trim()) return;
    setStatus("sending");
    try {
      const response = await fetch("/api/release-updates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), website }),
      });
      if (!response.ok) throw new Error("failed");
      setEmail("");
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="get-aktua" className="scroll-mt-20 px-5 py-20 sm:px-8 lg:py-28">
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] bg-[#041F1A] px-6 py-16 sm:px-12 lg:py-20">
        <TopoLines className="pointer-events-none absolute -right-24 -top-10 text-[#8BE8C7] opacity-[0.1]" />
        <div className="relative grid items-center gap-12 lg:grid-cols-[1fr_0.85fr]">
          <div className="reveal">
            <p className="eyebrow text-[#8BE8C7]">iOS release updates</p>
            <h2 className="section-title mt-3 text-4xl text-[#F3F7F5] sm:text-5xl">
              Want AKTUA on iPhone?
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#B8C8C3] md:text-lg">
              AKTUA is available now on Google Play. iOS TestFlight testing continues; join the list for App Store launch news.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" data-testid="store-badge-google-play" className="inline-flex min-h-[44px] items-center rounded-xl border border-[#8BE8C7]/25 px-4 text-sm font-bold text-[#8BE8C7] transition-colors hover:bg-[#8BE8C7]/10">
                Android <span className="ml-2 font-normal text-[#B8C8C3]">· Available now</span>
              </a>
              <span data-testid="store-badge-app-store" className="inline-flex min-h-[44px] items-center rounded-xl border border-[#8BE8C7]/25 px-4 text-sm font-bold text-[#8BE8C7]">
                iOS <span className="ml-2 font-normal text-[#B8C8C3]">· App Store pending</span>
              </span>
            </div>
          </div>

          <div className="reveal rounded-3xl border border-[#8BE8C7]/15 bg-[#062B24] p-6 sm:p-7" style={{ transitionDelay: "100ms" }}>
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#8BE8C7]/10 text-[#8BE8C7]"><Lock size={18} aria-hidden="true" /></span>
              <div>
                <p className="font-bold text-[#F3F7F5]">Get iOS release updates</p>
                <p className="text-xs text-[#819D95]">Release news only. Unsubscribe anytime.</p>
              </div>
            </div>
            {status === "done" ? (
              <p data-testid="launch-success-message" className="mt-5 rounded-2xl border border-[#8BE8C7]/30 bg-[#0B3A31] px-5 py-4 text-sm font-medium text-[#8BE8C7]">
                You are on the list. We will let you know when AKTUA reaches the App Store.
              </p>
            ) : (
              <form onSubmit={submit} className="mt-5 grid gap-3" data-testid="launch-form">
                <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
                  <label htmlFor="launch-website">Website</label>
                  <input id="launch-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} />
                </div>
                <label htmlFor="launch-email" className="sr-only">Email address</label>
                <input
                  id="launch-email"
                  data-testid="launch-email-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className="min-h-[48px] rounded-xl border border-[#8BE8C7]/25 bg-[#041F1A] px-5 text-sm text-[#F3F7F5] placeholder:text-[#819D95] focus:border-[#8BE8C7] focus:outline-none"
                />
                <button type="submit" data-testid="launch-submit-button" disabled={status === "sending"} className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[#8BE8C7] px-7 text-sm font-bold text-[#041F1A] transition-colors hover:bg-[#F3F7F5] disabled:opacity-60">
                  {status === "sending" ? "Joining..." : "Keep me updated"} <ArrowRight size={16} aria-hidden="true" />
                </button>
              </form>
            )}
            <p className="mt-3 text-xs leading-relaxed text-[#819D95]">See our <a href="/privacy-policy" className="underline underline-offset-2 hover:text-[#8BE8C7]">Privacy Policy</a>.</p>
            {status === "error" ? <p data-testid="launch-error-message" className="mt-3 text-sm font-medium text-[#FF8E86]">Something went wrong. Please try again.</p> : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Sections = () => (
  <>
    <StoreAvailability />
    <FinalCta />
  </>
);
